/**
 * HumanoidModel (zombie, skeleton, player) with vanilla setupAnim, plus the
 * armor layers (humanoid / humanoid_leggings equipment sheets).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat, type PartDef } from './core';

export interface HumanoidPose {
  limbSwing: number; limbAmt: number;
  /** model-space head rotation (rad): yRot already sign-converted, xRot positive = looking down */
  headY: number; headX: number;
  age: number;
  attack: number;
  /** zombie-style raised arms */
  zombieArms: boolean;
  /** bow aiming pose (both arms up) */
  bow: boolean;
  /** vanilla use-anim poses: SPEAR (trident raised to throw), CROSSBOW charge / loaded hold */
  useAnim?: '' | 'spear' | 'spear_charge' | 'spear_tired' | 'spear_drop' | 'crossbow_charge' | 'crossbow_hold' | 'eat';
  /** skeleton/player holding an item in the right hand (arm lowered a bit) */
  holding: boolean;
  /** the held item is a spear: jabs thrust the arm straight forward instead of swinging */
  spear?: boolean;
  /** off-hand item (shield) lowered at the side / raised across the chest (vanilla ArmPose.ITEM / BLOCK on the left arm) */
  holdingLeft?: boolean;
  blockingLeft?: boolean;
  sneaking: boolean;
  swimming: boolean;
  /** seated on a mount (riding): legs forward and apart, arms slightly lowered */
  sitting?: boolean;
  /** idle arm sway (zombie/skeleton); players don't sway */
  bob: boolean;
}

export type ArmorSlot = 'head' | 'chest' | 'legs' | 'feet';

export interface HumanoidOpts {
  sheet: string;
  /** 64x64 skin layout (separate left limbs + overlay layers) vs legacy 64x32 mirrored */
  modern: boolean;
  /** skeleton: 2px thick limbs */
  thin?: boolean;
  tw: number; th: number;
}

const PI = Math.PI;

export class Humanoid {
  readonly root = new THREE.Group();
  readonly mats: EntityMat[] = [];
  readonly head: THREE.Group; readonly body: THREE.Group;
  readonly rightArm: THREE.Group; readonly leftArm: THREE.Group;
  readonly rightLeg: THREE.Group; readonly leftLeg: THREE.Group;
  private baseMat: EntityMat;
  private armor: Partial<Record<ArmorSlot, { groups: THREE.Group[]; mats: EntityMat[] }>> = {};
  private armorKey = ['', '', '', ''];
  /** Tip of the right hand (vanilla ItemInHandLayer frame, true handedness, block units). */
  readonly rightHand = new THREE.Object3D();
  /** Same frame on the left arm (off-hand items, the shield). */
  readonly leftHand = new THREE.Object3D();

  constructor(private readonly env: EnvUniforms, private readonly o: HumanoidOpts, flat = false, srgbOut = false) {
    const { tw, th, modern } = o;
    const mat = (): EntityMat => {
      const m = entityMaterial(env, sheetTexture(o.sheet), { flat, srgbOut });
      this.mats.push(m);
      return m;
    };
    this.baseMat = mat();
    const aw = o.thin ? 2 : 4;
    const ax = o.thin ? -1 : -3;
    const lw = o.thin ? 2 : 4;
    const lx = o.thin ? -1 : -2;
    const cubes = (tex: [number, number], from: [number, number, number], size: [number, number, number], inf = 0, mirror = false): CubeDef[] =>
      [{ tex, from, size, inflate: inf, mirror }];
    const part = (cs: CubeDef[], pos: [number, number, number]): PartDef => ({ cubes: cs, pos });

    this.head = buildPart(part(cubes([0, 0], [-4, -8, -4], [8, 8, 8]), [0, 0, 0]), tw, th, this.baseMat);
    this.body = buildPart(part(cubes([16, 16], [-4, 0, -2], [8, 12, 4]), [0, 0, 0]), tw, th, this.baseMat);
    this.rightArm = buildPart(part(cubes([40, 16], [ax, -2, o.thin ? -1 : -2], [aw, 12, aw]), [o.thin ? -5 : -5, o.thin ? 2 : 2, 0]), tw, th, this.baseMat);
    const lax = o.thin ? -1 : -1;
    this.leftArm = buildPart(part(modern
      ? cubes([32, 48], [lax, -2, -2], [aw, 12, aw])
      : cubes([40, 16], [lax, -2, o.thin ? -1 : -2], [aw, 12, aw], 0, true), [5, 2, 0]), tw, th, this.baseMat);
    this.rightLeg = buildPart(part(cubes([0, 16], [lx, 0, o.thin ? -1 : -2], [lw, 12, lw]), [-1.9, 12, 0]), tw, th, this.baseMat);
    this.leftLeg = buildPart(part(modern
      ? cubes([16, 48], [lx, 0, -2], [lw, 12, lw])
      : cubes([0, 16], [lx, 0, o.thin ? -1 : -2], [lw, 12, lw], 0, true), [1.9, 12, 0]), tw, th, this.baseMat);
    if (o.thin) { this.rightLeg.position.set(-2, 12, 0); this.leftLeg.position.set(2, 12, 0); }

    if (modern) {
      // overlay layers (hat, jacket, sleeves, pants), inflated
      this.head.add(buildPart(part(cubes([32, 0], [-4, -8, -4], [8, 8, 8], 0.5), [0, 0, 0]), tw, th, this.baseMat));
      this.body.add(buildPart(part(cubes([16, 32], [-4, 0, -2], [8, 12, 4], 0.25), [0, 0, 0]), tw, th, this.baseMat));
      this.rightArm.add(buildPart(part(cubes([40, 32], [ax, -2, -2], [aw, 12, aw], 0.25), [0, 0, 0]), tw, th, this.baseMat));
      this.leftArm.add(buildPart(part(cubes([48, 48], [lax, -2, -2], [aw, 12, aw], 0.25), [0, 0, 0]), tw, th, this.baseMat));
      this.rightLeg.add(buildPart(part(cubes([0, 32], [lx, 0, -2], [lw, 12, lw], 0.25), [0, 0, 0]), tw, th, this.baseMat));
      this.leftLeg.add(buildPart(part(cubes([0, 48], [lx, 0, -2], [lw, 12, lw], 0.25), [0, 0, 0]), tw, th, this.baseMat));
    }
    for (const g of [this.head, this.body, this.rightArm, this.leftArm, this.rightLeg, this.leftLeg]) this.root.add(g);

    // hand attach: vanilla ItemInHandLayer pose (arm frame -> translate(-1/16,.125,-.625) after rotX(-90), rotY(180));
    // child scale 16 turns the pixel-space arm frame into vanilla block units (the flip stays, as in vanilla).
    const hand = this.rightHand;
    hand.scale.set(16, 16, 16);
    this.rightArm.add(hand);
    hand.userData.vanilla = true;
    this.setHandPose(hand, false);
    const lh = this.leftHand;
    lh.scale.set(16, 16, 16);
    this.leftArm.add(lh);
    this.setHandPose(lh, true);
  }

  private setHandPose(h: THREE.Object3D, left: boolean): void {
    // F * T(arm) * Rx(-90) * Ry(180) * T(-1/16, .125, -.625) — position expressed in the arm's pixel frame
    h.rotation.order = 'XYZ';
    const q = new THREE.Quaternion().setFromEuler(new THREE.Euler(-PI / 2, 0, 0))
      .multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, PI, 0)));
    // the rotated frame, then the local translate
    h.quaternion.copy(q);
    const t = new THREE.Vector3(left ? 1 : -1, 2, -10).applyQuaternion(q);
    h.position.copy(t);
  }

  setSkin(tex: THREE.Texture): void {
    for (const m of this.mats) if (m.uniforms.uMap.value === this.baseMat.uniforms.uMap.value) m.uniforms.uMap.value = tex;
    this.baseMat.uniforms.uMap.value = tex;
  }

  /** helmet/chest/legs/boots material ids: 'leather' | 'iron' | 'gold' | 'diamond' | '' */
  setArmor(materials: readonly string[]): void {
    for (let i = 0; i < 4; i++) {
      const key = materials[i] ?? '';
      if (key === this.armorKey[i]) continue;
      this.armorKey[i] = key;
      const slot = (['head', 'chest', 'legs', 'feet'] as const)[i];
      const old = this.armor[slot];
      if (old) {
        for (const g of old.groups) g.parent?.remove(g);
        for (const m of old.mats) { const k = this.mats.indexOf(m); if (k >= 0) this.mats.splice(k, 1); m.dispose(); }
        delete this.armor[slot];
      }
      if (key) this.armor[slot] = this.buildArmor(slot, key);
    }
  }

  private buildArmor(slot: ArmorSlot, key: string): { groups: THREE.Group[]; mats: EntityMat[] } {
    const groups: THREE.Group[] = [];
    const mats: EntityMat[] = [];
    const inflate = slot === 'legs' ? 0.5 : 1.0;
    const dir = slot === 'legs' ? 'humanoid_leggings' : 'humanoid';
    const addLayer = (tex: string, tint: number | undefined, parts: Array<[THREE.Group, CubeDef]>): void => {
      const m = entityMaterial(this.env, sheetTexture(`entity/equipment/${dir}/${tex}`), { tint, flat: this.baseMat.uniforms.uFlat.value > 0.5, srgbOut: 'OUT_SRGB' in (this.baseMat.defines ?? {}) });
      mats.push(m); this.mats.push(m);
      for (const [parent, cube] of parts) {
        const g = buildPart({ cubes: [{ ...cube, inflate }] }, 64, 32, m);
        parent.add(g); groups.push(g);
      }
    };
    const parts: Array<[THREE.Group, CubeDef]> = [];
    const arm = (right: boolean): CubeDef => ({ tex: [40, 16], from: [right ? -3 : -1, -2, -2], size: [4, 12, 4], mirror: !right });
    const leg = (right: boolean): CubeDef => ({ tex: [0, 16], from: [-2, 0, -2], size: [4, 12, 4], mirror: !right });
    const bodyC: CubeDef = { tex: [16, 16], from: [-4, 0, -2], size: [8, 12, 4] };
    if (slot === 'head') parts.push([this.head, { tex: [0, 0], from: [-4, -8, -4], size: [8, 8, 8] }]);
    else if (slot === 'chest') parts.push([this.body, bodyC], [this.rightArm, arm(true)], [this.leftArm, arm(false)]);
    else if (slot === 'legs') parts.push([this.body, bodyC], [this.rightLeg, leg(true)], [this.leftLeg, leg(false)]);
    else parts.push([this.rightLeg, leg(true)], [this.leftLeg, leg(false)]);
    addLayer(key === 'leather' ? 'leather' : key, key === 'leather' ? 0xa06540 : undefined, parts);
    if (key === 'leather') addLayer('leather_overlay', undefined, parts);
    return { groups, mats };
  }

  /** vanilla HumanoidModel.setupAnim + attack + sneaking/swimming/bow poses. */
  animate(p: HumanoidPose): void {
    const { head, body, rightArm: ra, leftArm: la, rightLeg: rl, leftLeg: ll } = this;
    const modernPlayer = this.o.modern;
    head.rotation.set(p.headX, p.headY, 0);
    body.rotation.set(0, 0, 0);
    body.position.set(0, 0, 0);
    head.position.set(0, 0, 0);
    ra.position.set(-5, 2, 0); la.position.set(5, 2, 0);
    rl.position.set(this.o.thin ? -2 : -1.9, 12, 0); ll.position.set(this.o.thin ? 2 : 1.9, 12, 0);
    const s = p.limbSwing * 0.6662, a = p.limbAmt;
    ra.rotation.set(Math.cos(s + PI) * a, 0, 0);
    la.rotation.set(Math.cos(s) * a, 0, 0);
    rl.rotation.set(Math.cos(s) * 1.4 * a, 0, 0);
    ll.rotation.set(Math.cos(s + PI) * 1.4 * a, 0, 0);

    if (p.zombieArms) {
      const t = p.attack;
      const f = Math.sin(t * PI), f1 = Math.sin((1 - (1 - t) * (1 - t)) * PI);
      ra.rotation.z = 0; la.rotation.z = 0;
      ra.rotation.y = -(0.1 - f * 0.6); la.rotation.y = 0.1 - f * 0.6;
      const f2 = -PI / 1.5;
      ra.rotation.x = f2 + f * 1.2 - f1 * 0.4; la.rotation.x = f2 + f * 1.2 - f1 * 0.4;
    } else if (p.useAnim === 'spear') {
      // HumanoidModel.poseRightArm THROW_SPEAR
      ra.rotation.x = ra.rotation.x * 0.5 - PI; ra.rotation.y = 0;
    } else if (p.useAnim === 'spear_charge' || p.useAnim === 'spear_tired' || p.useAnim === 'spear_drop') {
      // spear charge: arm levelled forward, the shaft couched under it; tired = sagging and shaking; dropped = hanging
      const tired = p.useAnim === 'spear_tired', drop = p.useAnim === 'spear_drop';
      ra.rotation.x = (drop ? -0.5 : tired ? -1.1 + Math.sin(p.age * 1.7) * 0.08 : -1.4) + head.rotation.x * (drop ? 0.1 : 0.5);
      ra.rotation.y = -0.2; la.rotation.x = la.rotation.x * 0.5 - 0.3;
    } else if (p.useAnim === 'crossbow_charge') {
      // AnimationUtils.animateCrossbowCharge (mid-charge)
      ra.rotation.y = -0.8; ra.rotation.x = -0.97079635;
      la.rotation.x = -0.97079635 + (-PI / 2 + 0.97079635) * 0.6; la.rotation.y = 0.4 + 0.45 * 0.6;
    } else if (p.useAnim === 'crossbow_hold') {
      // AnimationUtils.animateCrossbowHold
      ra.rotation.y = -0.3 + head.rotation.y; la.rotation.y = 0.6 + head.rotation.y;
      ra.rotation.x = -PI / 2 + head.rotation.x + 0.1; la.rotation.x = -1.5 + head.rotation.x;
    } else if (p.bow) {
      ra.rotation.y = -0.1 + head.rotation.y; ra.rotation.x = -PI / 2 + head.rotation.x;
      la.rotation.y = 0.1 + head.rotation.y + 0.4; la.rotation.x = -PI / 2 + head.rotation.x;
    } else if (p.useAnim === 'eat') {
      // raised to the mouth, the hand chewing along (Bedrock eat/drink pose)
      ra.rotation.x = -1.25 + Math.sin(p.age * 1.3) * 0.08; ra.rotation.y = -0.35;
    } else if (p.holding) {
      ra.rotation.x = ra.rotation.x * 0.5 - PI / 10;
    }
    if (p.blockingLeft) { la.rotation.x = la.rotation.x * 0.5 - 0.9424779; la.rotation.y = PI / 6; }
    else if (p.holdingLeft && !p.bow && !p.useAnim) la.rotation.x = la.rotation.x * 0.5 - PI / 10;
    if (p.sneaking) {
      body.rotation.x = 0.5;
      ra.rotation.x += 0.4; la.rotation.x += 0.4;
      rl.position.z = 4; ll.position.z = 4;
      rl.position.y = 12.2; ll.position.y = 12.2;
      head.position.y = 4.2; body.position.y = 3.2;
      ra.position.y = 5.2; la.position.y = 5.2;
    }
    if (p.sitting) {
      // vanilla riding pose (HumanoidModel riding): arms -18deg, legs -72deg splayed
      ra.rotation.x += -PI / 5; la.rotation.x += -PI / 5;
      rl.rotation.set(-1.4137167, PI / 10, 0.07853982); ll.rotation.set(-1.4137167, -PI / 10, -0.07853982);
    }
    if (p.spear && p.attack > 0 && !p.useAnim) {
      // stab: the arm punches level along the look direction and back
      const th = Math.sin(Math.min(1, p.attack) * PI);
      ra.rotation.x = ra.rotation.x * (1 - th) + (-1.45 + head.rotation.x * 0.5) * th;
      ra.rotation.y = -0.15 * th;
    } else if (p.attack > 0 && !p.zombieArms) {
      const t = p.attack;
      const by = Math.sin(Math.sqrt(t) * PI * 2) * 0.2;
      body.rotation.y = by;
      ra.position.z = Math.sin(by) * 5; ra.position.x = -Math.cos(by) * 5;
      la.position.z = -Math.sin(by) * 5; la.position.x = Math.cos(by) * 5;
      ra.rotation.y += by; la.rotation.y += by; la.rotation.x += by;
      let f = 1 - t; f *= f; f *= f; f = 1 - f;
      const f2 = Math.sin(f * PI);
      const f3 = Math.sin(t * PI) * -(head.rotation.x - 0.7) * 0.75;
      ra.rotation.x -= f2 * 1.2 + f3;
      ra.rotation.y += by * 2;
      ra.rotation.z += Math.sin(t * PI) * -0.4;
    }
    if (p.bob) {
      ra.rotation.z += Math.cos(p.age * 0.09) * 0.05 + 0.05;
      la.rotation.z -= Math.cos(p.age * 0.09) * 0.05 + 0.05;
      ra.rotation.x += Math.sin(p.age * 0.067) * 0.05;
      la.rotation.x -= Math.sin(p.age * 0.067) * 0.05;
    }
    if (p.swimming) {
      // crawl stroke: arms circle overhead, legs flutter
      const sw = p.limbSwing * 0.33;
      ra.rotation.set(-PI * 0.9 + Math.sin(sw) * 0.9, 0, 0.15 + Math.cos(sw) * 0.2);
      la.rotation.set(-PI * 0.9 + Math.sin(sw + PI) * 0.9, 0, -0.15 - Math.cos(sw + PI) * 0.2);
      rl.rotation.set(Math.cos(sw) * 0.5, 0, 0);
      ll.rotation.set(Math.cos(sw + PI) * 0.5, 0, 0);
      head.rotation.x = -PI / 4 + head.rotation.x * 0.3;
    }
    void modernPlayer;
  }
}
